/**
 * 实时更新全局 Provider —— 整个应用只挂一次（app.tsx 的 childrenRender 中、TabLayout 外层）。
 *
 * 启动条件：开关开启（isRealtimeEnabled）且当前有 token。
 * 卸载、登出、运行期关闭开关都会断开连接并清空脏标记。
 */
import { type ReactNode, useEffect, useRef } from 'react';
import { getStoredAuthToken } from '@/requestErrorConfig';
import { clearReferenceCache } from '@/services/businessReference';
import { isRealtimeEnabled, REALTIME_ENABLED_CHANGE_EVENT } from './config';
import { markDirty, resetDirtyStore } from './dirtyStore';
import { isReferenceChange, type RealtimeChangeEvent } from './eventTypes';
import {
  createRealtimeConnection,
  type RealtimeConnection,
} from './realtimeConnection';

export interface RealtimeProviderProps {
  children?: ReactNode;
}

/** 联调期在 Console 打印连接状态与收到的帧；生产构建不输出。 */
const isDev = process.env.NODE_ENV !== 'production';

export default function RealtimeProvider({ children }: RealtimeProviderProps) {
  const connectionRef = useRef<RealtimeConnection | null>(null);

  useEffect(() => {
    const receiveChange = (event: RealtimeChangeEvent) => {
      if (isReferenceChange(event)) clearReferenceCache();
      markDirty(event);
    };
    const start = () => {
      if (connectionRef.current) return;
      if (!isRealtimeEnabled()) return;
      if (!getStoredAuthToken()) return;
      const connection = createRealtimeConnection({
        onEvent: (event) => {
          if (isDev) console.info('[realtime] event', event);
          receiveChange(event);
        },
        onStatusChange: (status) => {
          if (isDev) console.info('[realtime] status ->', status);
          if (status !== 'open') return;
          // 首次连接前和断线期间可能漏通知，连通后让只读页面补取一次。
          receiveChange({ scope: 'all' });
        },
      });
      connectionRef.current = connection;
      connection.start();
    };

    const stop = () => {
      connectionRef.current?.stop('provider');
      connectionRef.current = null;
      resetDirtyStore();
    };

    const handleToggle = () => {
      if (isRealtimeEnabled()) {
        start();
      } else {
        stop();
      }
    };

    start();
    window.addEventListener('logout', stop);
    window.addEventListener(REALTIME_ENABLED_CHANGE_EVENT, handleToggle);
    window.addEventListener('storage', handleToggle);

    return () => {
      window.removeEventListener('logout', stop);
      window.removeEventListener(REALTIME_ENABLED_CHANGE_EVENT, handleToggle);
      window.removeEventListener('storage', handleToggle);
      connectionRef.current?.stop('unmount');
      connectionRef.current = null;
      resetDirtyStore();
    };
  }, []);

  return <>{children}</>;
}
